import {
  Box,
  Chip,
  Grid,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { formatNumber } from "../../../../../../infrastructure/utils/formatters";
import { ManagementWidgetShell, severityChipColor, severityLabel } from "../managementDashboardWidgets";
import { buildDrillDownHref, pick } from "../managementDashboardUtils";

const scoreColor = (score) => {
  if (score >= 80) return "success";
  if (score >= 60) return "warning";
  return "error";
};

const HealthScoreWidget = ({ loading, error, data, onRetry }) => {
  const overall = pick(data, "overallScore", "OverallScore");
  const severity = pick(data, "overallSeverity", "OverallSeverity");
  const categories = data?.categoryScores || data?.CategoryScores || [];
  const factors = data?.criticalFactors || data?.CriticalFactors || [];

  return (
    <ManagementWidgetShell
      title="امتیاز سلامت مدیریت"
      subheader="ترکیب خط لوله، SLA، پیمانکار، لندتک و رشد"
      loading={loading}
      error={error}
      onRetry={onRetry}
    >
      {overall != null ? (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                textAlign: "center",
                p: 2,
                borderRadius: 2,
                bgcolor: "background.default",
              }}
            >
              <Typography variant="overline" color="text.secondary">
                امتیاز کلی
              </Typography>
              <Typography
                variant="h2"
                fontWeight={800}
                color={`${scoreColor(overall)}.main`}
                sx={{ my: 1 }}
              >
                {formatNumber(overall, 0)}
              </Typography>
              <Chip
                size="small"
                color={severityChipColor(severity)}
                label={severityLabel(severity)}
              />
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Number(overall))}
                color={scoreColor(overall)}
                sx={{ mt: 2, height: 8, borderRadius: 4 }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={8}>
            <Typography fontWeight={600} fontSize={14} sx={{ mb: 1 }}>
              امتیاز دسته‌ها
            </Typography>
            <Stack spacing={1}>
              {categories.map((cat) => {
                const code = pick(cat, "code", "Code");
                const score = pick(cat, "score", "Score") ?? 0;
                const href = buildDrillDownHref(pick(cat, "drillDown", "DrillDown"));
                return (
                  <Box key={code}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      {href ? (
                        <Link component={RouterLink} to={href} underline="hover" fontSize={13}>
                          {pick(cat, "title", "Title")}
                        </Link>
                      ) : (
                        <Typography fontSize={13}>{pick(cat, "title", "Title")}</Typography>
                      )}
                      <Typography fontWeight={700} fontSize={13}>
                        {formatNumber(score, 0)}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {" "}
                          / 100
                        </Typography>
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Number(score))}
                      color={scoreColor(score)}
                      sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Grid>

          {factors.length ? (
            <Grid item xs={12}>
              <Typography fontWeight={600} fontSize={14} sx={{ mb: 1 }}>
                عوامل بحرانی
              </Typography>
              <Stack spacing={1}>
                {factors.slice(0, 5).map((factor) => {
                  const code = pick(factor, "code", "Code");
                  const href = buildDrillDownHref(pick(factor, "drillDown", "DrillDown"));
                  return (
                    <Stack
                      key={code}
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ sm: "center" }}
                      justifyContent="space-between"
                      sx={{ borderBottom: "1px solid", borderColor: "divider", pb: 1 }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          size="small"
                          color={severityChipColor(pick(factor, "severity", "Severity"))}
                          label={severityLabel(pick(factor, "severity", "Severity"))}
                        />
                        <Box>
                          <Typography fontWeight={600} fontSize={13}>
                            {pick(factor, "title", "Title")}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {pick(factor, "description", "Description")}
                          </Typography>
                        </Box>
                      </Stack>
                      {href ? (
                        <Link component={RouterLink} to={href} variant="caption">
                          مشاهده
                        </Link>
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            </Grid>
          ) : null}
        </Grid>
      ) : (
        !loading && !error && (
          <Typography color="text.secondary">داده‌ای برای محاسبه امتیاز سلامت موجود نیست.</Typography>
        )
      )}
    </ManagementWidgetShell>
  );
};

export default HealthScoreWidget;
